import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Save, Loader2, CheckCircle, XCircle, FileSignature } from 'lucide-react';
import { DocuSignSettings } from './DocuSignSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PandaDocCard = () => {
  const { token } = useAuth();
  const [environment, setEnvironment] = useState('sandbox');
  const [apiKey, setApiKey] = useState('');
  const [hasKey, setHasKey] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    axios.get(API_URL + '/api/esign/settings?authorization=' + token)
      .then((r) => {
        setEnvironment(r.data.pandadoc.environment || 'sandbox');
        setHasKey(r.data.pandadoc.has_api_key);
        setConfigured(r.data.pandadoc.configured);
      })
      .catch(() => {});
  }, [token]);

  const save = () => {
    setSaving(true);
    axios.post(API_URL + '/api/pandadoc/settings?authorization=' + token, { environment, api_key: apiKey || null })
      .then((r) => { toast.success('PandaDoc settings saved'); setConfigured(r.data.configured); if (apiKey) { setHasKey(true); setApiKey(''); } })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  const test = () => {
    setTesting(true);
    setTestResult(null);
    axios.post(API_URL + '/api/pandadoc/test?authorization=' + token)
      .then((r) => setTestResult(r.data))
      .catch(() => setTestResult({ success: false, message: 'Test request failed' }))
      .finally(() => setTesting(false));
  };

  const webhookUrl = window.location.origin + '/api/pandadoc/webhook';

  return (
    <Card data-testid="pandadoc-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-emerald-500" />PandaDoc eSignature</CardTitle>
            <CardDescription>Get an API key from PandaDoc → Settings → Integrations → API. Sandbox keys are free for testing.</CardDescription>
          </div>
          {configured ? <Badge className="bg-green-100 text-green-700">Configured</Badge> : <Badge variant="outline" className="text-amber-600 border-amber-300">Not configured</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Environment</Label>
            <Select value={environment} onValueChange={setEnvironment}>
              <SelectTrigger data-testid="pd-environment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">Sandbox key (testing)</SelectItem>
                <SelectItem value="production">Production key (live)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>API Key {hasKey && <span className="text-green-600 text-xs">(saved — paste again only to replace)</span>}</Label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="PandaDoc API key" data-testid="pd-api-key" />
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={save} disabled={saving} className="bg-[#015492] hover:bg-[#013f6e]" data-testid="pd-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Settings
          </Button>
          <Button variant="outline" onClick={test} disabled={testing || !configured} data-testid="pd-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Test Connection
          </Button>
        </div>
        {testResult && (
          <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} data-testid="pd-test-result">
            {testResult.success ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <p>{testResult.message}</p>
          </div>
        )}
        <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-600 space-y-1">
          <p className="font-semibold text-slate-700">Optional — real-time status webhook:</p>
          <p>In PandaDoc → Developer Dashboard → Webhooks, add: <code className="bg-white px-1.5 py-0.5 rounded border">{webhookUrl}</code> (events: document state changed, document completed PDF ready). Without it, use the refresh button in C&I History — statuses still update on demand.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const ESignSettings = () => {
  const { token } = useAuth();
  const [provider, setProvider] = useState('docusign');
  const [savingProvider, setSavingProvider] = useState(false);

  useEffect(() => {
    axios.get(API_URL + '/api/esign/settings?authorization=' + token)
      .then((r) => setProvider(r.data.provider || 'docusign'))
      .catch(() => {});
  }, [token]);

  const changeProvider = (p) => {
    setProvider(p);
    setSavingProvider(true);
    axios.post(API_URL + '/api/esign/settings?authorization=' + token, { provider: p })
      .then((r) => toast.success(r.data.message))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to update provider'))
      .finally(() => setSavingProvider(false));
  };

  return (
    <div className="space-y-6">
      <Card data-testid="esign-provider-card">
        <CardHeader>
          <CardTitle>Active eSignature Provider</CardTitle>
          <CardDescription>Choose which provider is used when staff click "Send for Signature" on C&I contracts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button variant={provider === 'docusign' ? 'default' : 'outline'} disabled={savingProvider} onClick={() => changeProvider('docusign')} className={provider === 'docusign' ? 'bg-[#015492]' : ''} data-testid="esign-provider-docusign">DocuSign</Button>
            <Button variant={provider === 'pandadoc' ? 'default' : 'outline'} disabled={savingProvider} onClick={() => changeProvider('pandadoc')} className={provider === 'pandadoc' ? 'bg-[#015492]' : ''} data-testid="esign-provider-pandadoc">PandaDoc</Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">Contracts already sent keep tracking through the provider they were sent with.</p>
        </CardContent>
      </Card>
      <DocuSignSettings />
      <PandaDocCard />
    </div>
  );
};
